<script context="module">
  import PageHead from "./../Components/PageHead.svelte";
  import Breadcrumb from "./Partials/Breadcrumb.svelte";
  import WarnAlert from "../Shared/WarnAlert.svelte";
  export { default as layout } from "../Shared/Layout.svelte";
</script>

<script>
  import { useForm, router, page, inertia } from "@inertiajs/svelte";

  import { writable } from "svelte/store";
  import { formatGeneral } from "cleave-zen";

  export let reminder, pending_vehicle;

  $: vehicles = writable(reminder.vehicles);
  $: emails = writable(reminder.emails);
  $: phone_numbers = writable(reminder.phone_numbers);

  const form = useForm("reminderSearchForm", {
    type: null,
    vin: null,
    plate_number: null,
    email: null,
    phone: null,
  });

  let confirm = {
    email: false,
    phone_numbers: false,
  };

  const phoneConfirmForm = useForm("phoneConfirmForm", {
    codes: {},
  });

  const vehicleConfirmForm = useForm("vehicleConfirmForm", {
    confirm: null,
  });

  function handleSubmit(type) {
    $form.clearErrors();
    $form.type = type;
    $form.post(route("reminders.change"), {
      preserveScroll: true,
      onError: (e) => {
        if (e.vehicle) {
          $form.setError("vehicle", e.vehicle[0]);
        }
      },
      onSuccess: () => {
        $form.reset();
        router.reload({ replace: true, only: [type] });
        confirm[type] = true;
      },
    });
  }

  function handlePhoneConfirmSubmit(id) {
    router.post(
      route("reminders.change.confirm_phone"),
      {
        id: id,
        code: $phoneConfirmForm.codes[id],
      },
      {
        preserveScroll: true,
        onBefore: () => ($phoneConfirmForm.processing = true),
        onFinish: () => ($phoneConfirmForm.processing = false),
        onSuccess: () => (confirm.phone = false),
        onError: (e) => {
          if (e.confirm_phone) {
            for (let id in e.confirm_phone) {
              $phoneConfirmForm.setError(id, e.confirm_phone[id]);
            }
          }
        },
      }
    );
  }

  function handleVehicleConfirmSubmit(confirm) {
    router.post(
      route("reminders.change.confirm_vehicle"),
      {
        confirm: confirm,
      },
      {
        preserveScroll: true,
        onBefore: () => ($vehicleConfirmForm.processing = true),
        onFinish: () => ($vehicleConfirmForm.processing = false),
        onSuccess: () => $form.reset("vin", "plate_number"),
      }
    );
  }

  const handleRemoveEntry = (type, id) => {
    const collections = {
      vehicle: vehicles,
      email: emails,
      phone: phone_numbers,
    };

    const collectionToUpdate = collections[type];
    if (collectionToUpdate) {
      collectionToUpdate.update((currentItems) =>
        currentItems.filter((item) => item.id !== id)
      );
    }

    router.delete(route("reminders.change.remove", { id, type }), {
      preserveScroll: true,
    });
  };

  function inputVIN(e) {
    $form.vin = formatGeneral(e.target.value, {
      blocks: [4],
      numericOnly: true,
    });
  }

  function inputPhone(e) {
    $form.phone = formatGeneral(e.target.value, {
      blocks: [3, 3, 4],
      delimiter: "-",
      numericOnly: true,
    });
  }
</script>

<PageHead>
  <svelte:fragment slot="title">Renewal Reminders</svelte:fragment>
  <svelte:fragment slot="tagline">
    Below are the vehicles you've requested renewal reminders for and the
    notification methods you've chosen.<br />You may add/remove vehicles from
    your reminders or change your notification methods.
  </svelte:fragment>
  <Breadcrumb slot="breadcrumb">
    <span slot="home"></span>
    <span slot="reminders"></span>
    <span slot="reminders_change"></span>
  </Breadcrumb>
</PageHead>

<div class="row">
  <div class="col-12">
    <div class="card mb-2">
      <div class="card-header">
        <h3 class="fs-5 mb-0 py-2">Your Vehicles</h3>
      </div>
      <div class="list-group list-group-flush">
        {#each $vehicles as vehicle}
          <div
            class="list-group-item d-flex align-items-center justify-content-between"
          >
            <div>
              {vehicle.year}
              {vehicle.make}
              {vehicle.model}
            </div>
            <div>
              <button
                class="btn btn-outline-danger btn-sm"
                on:click={() => handleRemoveEntry("vehicle", vehicle.id)}
              >
                <i class="bi bi-trash"></i>
                <span class="d-none d-md-inline-block">&nbsp; Remove</span>
              </button>
            </div>
          </div>
        {/each}

        {#if pending_vehicle}
          <div
            class="list-group-item list-group-item-secondary align-items-center text-center justify-content-center"
          >
            <div class="w-100">
              <span class="text-primary">Is this the correct vehicle?</span>
            </div>
            <div class="py-2">
              {pending_vehicle.Year}
              {pending_vehicle.Make}
              {pending_vehicle.Model}&nbsp;
            </div>
            <div class="mt-2">
              <button
                class="btn btn-outline-success btn-sm"
                disabled={$vehicleConfirmForm.processing}
                on:click={() => handleVehicleConfirmSubmit(true)}
                ><i class="bi bi-check"></i> Yes, add this vehicle to my reminders.</button
              >
              <button
                class="btn btn-outline-danger btn-sm mt-2 mt-md-0"
                disabled={$vehicleConfirmForm.processing}
                on:click={() => handleVehicleConfirmSubmit(false)}
                ><i class="bi bi-trash"></i> No, this isn't the right vehicle.</button
              >
            </div>
          </div>
        {/if}
      </div>
      {#if !pending_vehicle}
        <div class="card-footer">
          <div class="row">
            <div class="col-12 small text-muted">
              Enter the last 4 of your VIN number and license plate number of
              your vehicle(s) to receive email and/or text message reminders.
            </div>
            <div class="w-100 py-2"></div>
            {#if $form.errors.vehicle}
              <div class="col-7">
                <div class="alert alert-danger py-2">
                  {$form.errors.vehicle}
                </div>
              </div>
              <div class="w-100"></div>
            {/if}
            <div class="col-md-3">
              <div class="input-group">
                <span class="input-group-text bg-secondary-subtle"
                  >VIN Last 4</span
                >
                <input
                  type="text"
                  class="form-control border border-secondary-subtle"
                  class:is-invalid={$form.errors.vin}
                  placeholder="0000"
                  id="vinId"
                  minlength="4"
                  name="vin"
                  bind:value={$form.vin}
                  on:input={inputVIN}
                />
                {#if $form.errors.vin}
                  <div class="invalid-feedback">
                    {$form.errors.vin}
                  </div>
                {/if}
              </div>
            </div>
            <div class="col-md-3 mt-1 mt-md-0">
              <div class="input-group">
                <span class="input-group-text bg-secondary-subtle"
                  >Plate Number</span
                >
                <input
                  type="text"
                  class:is-invalid={$form.errors.plate_number}
                  class="form-control input-license-plate border border-secondary-subtle"
                  maxlength="7"
                  placeholder="000000"
                  id="licensePlateNumber"
                  name="plate_number"
                  bind:value={$form.plate_number}
                />
                {#if $form.errors.plate_number}
                  <div class="invalid-feedback">
                    {$form.errors.plate_number}
                  </div>
                {/if}
              </div>
            </div>
            <div class="col-md-auto mt-2 mt-md-0 text-center">
              <button
                class="btn btn-outline-success w-100"
                disabled={$form.processing}
                on:click={() => handleSubmit("vehicle")}>Add Vehicle</button
              >
            </div>
          </div>
        </div>
      {/if}
    </div>

    <div class="card mb-2">
      <div class="card-header">
        <h3 class="fs-5 mb-0 py-2">Your Email Addresses</h3>
      </div>
      <div class="list-group list-group-flush" id="emailList">
        {#if $emails.length}
          {#each $emails as email}
            <div
              class="list-group-item d-flex align-items-center justify-content-between row g-0"
            >
              <div class="col-auto">
                {#if !email.verified}
                  <span
                    class="small text-warning-emphasis border-warning-subtle border-2 p-1 border rounded-2"
                    >Pending</span
                  >&nbsp;
                {/if}
              </div>

              <div
                class="col text-truncate px-2"
                class:text-warning-emphasis={!email.verified}
              >
                {email.masked}
              </div>
              <div class="col-auto">
                <button
                  class="btn btn-outline-danger btn-sm"
                  on:click={() => handleRemoveEntry("email", email.id)}
                >
                  <i class="bi bi-trash"></i>
                  <span class="d-none d-md-inline-block">&nbsp; Remove</span>
                </button>
              </div>
            </div>
          {/each}
        {:else}
          <div class="list-group-item">
            You have no email addresses configured.
          </div>
        {/if}
      </div>
      <div class="card-footer">
        <WarnAlert
          bind:show={confirm.email}
          message={$page.props.messages.confirm_email_sent}
        />
        <div class="row">
          <div class="col-12 small text-muted">
            Enter the email address(es) to receive your Registration Renewal
            notification. If signing up for an email Renewal Reminder you will
            no longer receive a mailed Renewal Reminder notice.
          </div>
          <div class="w-100 py-2"></div>
          <div class="col-12 col-md-6">
            <div class="input-group">
              <span class="input-group-text bg-secondary-subtle"
                >Email Address</span
              >
              <input
                type="text"
                class="form-control border border-secondary-subtle"
                class:is-invalid={$form.errors.email}
                placeholder="you@example.com"
                id="emailId"
                name="email"
                bind:value={$form.email}
              />
              {#if $form.errors.email}
                <div class="invalid-feedback text-center">
                  {$form.errors.email}
                </div>
              {/if}
            </div>
          </div>
          <div class="col-md-auto mt-2 mt-md-0 text-center">
            <button
              class="btn btn-outline-success w-100"
              disabled={$form.processing}
              on:click={() => handleSubmit("email")}>Add Email Address</button
            >
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-2">
      <div class="card-header">
        <h3 class="fs-5 mb-0 py-2">Your Phone Numbers</h3>
      </div>
      <div class="list-group list-group-flush" id="phoneList">
        {#if $phone_numbers.length}
          {#each $phone_numbers as phone}
            <div
              class="list-group-item d-flex align-items-center justify-content-start row g-0"
            >
              <div class="col-auto order-0">
                {#if !phone.verified}
                  <span
                    class="small text-warning-emphasis border-warning-subtle border-2 p-1 border rounded-2"
                    >Pending</span
                  >&nbsp;
                {/if}
              </div>
              <div
                class="col-auto col-md order-1 justify-content-start px-2"
                class:text-warning-emphasis={!phone.verified}
              >
                {phone.masked}
              </div>
              {#if !phone.verified}
                <div class="col-12 col-md-3 mt-2 mt-md-0 order-3 order-md-2">
                  <div class="">
                    <div class="input-group input-group-sm">
                      <span
                        class="input-group-text bg-secondary-subtle form-control-sm"
                        >Verification Code</span
                      >
                      <input
                        type="text"
                        class="form-control border border-secondary-subtle"
                        class:is-invalid={$phoneConfirmForm.errors[phone.id]}
                        placeholder="000000"
                        id="phoneId"
                        bind:value={$phoneConfirmForm.codes[phone.id]}
                      />
                      <button
                        class="btn btn-outline-secondary"
                        type="button"
                        disabled={$phoneConfirmForm.processing}
                        on:click|preventDefault={() =>
                          handlePhoneConfirmSubmit(phone.id)}>Confirm</button
                      >
                      {#if $phoneConfirmForm.errors[phone.id]}
                        <div class="invalid-feedback text-center">
                          {$phoneConfirmForm.errors[phone.id]}
                        </div>
                      {/if}
                    </div>
                  </div>
                </div>
              {/if}
              <div
                class="col col-md order-2 order-md-3 d-flex justify-content-end"
              >
                <button
                  class="btn btn-outline-danger btn-sm"
                  on:click={() => handleRemoveEntry("phone", phone.id)}
                >
                  <i class="bi bi-trash"></i>
                  <span class="d-none d-md-inline-block">&nbsp; Remove</span>
                </button>
              </div>
            </div>
          {/each}
        {:else}
          <div class="list-group-item">
            You have no phone numbers configured.
          </div>
        {/if}
      </div>
      <div class="card-footer">
        <WarnAlert
          bind:show={confirm.phone}
          message={$page.props.messages.confirm_phone_sent}
        />
        <div class="row">
          <div class="col-12 small text-muted">
            Enter your Cell Phone Number to receive text message reminders. You
            will continue to receive a mailed Renewal notification with your
            text message renewal reminder notification.
          </div>
          <div class="w-100 py-2"></div>
          <div class="col-md-4">
            <div class="input-group">
              <span class="input-group-text bg-secondary-subtle"
                >Phone Number</span
              >
              <input
                type="text"
                class="form-control border border-secondary-subtle"
                class:is-invalid={$form.errors.phone}
                placeholder="xxx-xxx-xxxx"
                id="phoneId"
                name="phone"
                bind:value={$form.phone}
                on:input={inputPhone}
              />
              {#if $form.errors.phone}
                <div class="invalid-feedback">
                  {$form.errors.phone}
                </div>
              {/if}
            </div>
          </div>
          <div class="col-md-auto mt-2 mt-md-0 text-center">
            <button
              class="btn btn-outline-success w-100"
              id="addPhoneNumber"
              disabled={$form.processing}
              on:click={() => handleSubmit("phone")}>Add Phone Number</button
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
<div class="row g-2 justify-content-between my-1">
  <div class="col-auto col-md-4 d-md-grid">
    <a
      href={window.route("reminders.index")}
      use:inertia
      class="btn btn-secondary">Restart</a
    >
  </div>
  <div class="col-auto col-md-4 d-md-grid">
    <a href={window.route("home")} class="btn btn-primary" use:inertia
      >Return to Main Menu</a
    >
  </div>
</div>
